<template>
  <label class="text_input--container" :class="containerClasses" :style="{'--click-position': clickPosition}">
    <input :disabled="disabled" :name="name" ref="input" :type="type" v-model="currentValue"
           @input="$emit('input', currentValue)" @mousedown="mouseDownHandler" @focus="focusHandler" @blur="blurHandler">
    <span v-if="label.length" class="text_input--label">{{ label }}</span>
    <span v-if="errorMsg.length" class="text_input--error" @click="errorClickHandler"
          @animationend="errorAnimationEndHandler">
      <span v-if="errorIcon" class="text_input--error_icon material-icons">{{ errorIcon }}</span>
      {{ errorMsg }}
    </span>

    <a v-if="type === 'date' || type === 'month'" class="text_input--date_clear material-icons tooltip-dock_left"
       href="#" data-tooltip="Clear" @click.prevent.stop="dateClearClickHandler">
    </a>
  </label>
</template>

<script>
/**
 * Material design inspired text input.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.1.0
 * @copyright  Copyright (C) 2021, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2021-12-30 (1.1.0) (jhoward)
 *      - No longer mutating `value` prop. Use @input in parent component to update value.
 */

export default {
  name: "text-input",
  props: {
    label: {
      type: String,
      default: ''
    },
    errorMsg: {
      type: String,
      default: ''
    },
    errorIcon: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    value: {
      type: String,
      default: ''
    },
    validationIgnore: {
      type: Boolean,
      default: false
    },
    validationRegex: {
      type: String,
      default: ''
    },
    validationRegexMod: {
      type: String,
      default: 'gi'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    wide: {
      type: Boolean,
      default: false
    },
    mod: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      currentValue: this.value,
      hasChanged: this.disabled,
      active: false,
      error: false,
      errorBounce: false,
      clickPosition: '50%',
      allowedMods: ['bg', 'outline', ''] //outline is default
    };
  },
  computed: {
    containerClasses: function () {
      return {
        'text_input--container-required': this.required,
        'text_input--container-filled': this.filled,
        'text_input--container-disabled': this.disabled,
        'text_input--container-error': this.error,
        'text_input--container-error_bounce': this.errorBounce,
        'text_input--container-active': this.active,
        'text_input--container-wide': this.wide,
        'text_input--container-search': this.type === 'search',
        'text_input--container-bg': this.mod === 'bg'
      };
    },
    filled: function () {
      return this.currentValue.length;
    },
  },
  watch: {
    currentValue: function () {
      this.hasChanged = true;
      this.validate();
    }
  },
  methods: {

    validate: function (force) {
      const forceValidation = (this.filled || force);
      let isValid = true;
      let runValidation = (forceValidation && !this.hasChanged && this.required) || (this.hasChanged && !this.validationIgnore);

      if (runValidation) {
        if (this.required && forceValidation) {
          if (this.currentValue === '') {
            isValid = false;
          } else if (this.validationRegex.length) {
            const regExp = new RegExp(this.validationRegex, this.validationRegexMod);
            if (!regExp.test(this.currentValue)) {
              isValid = false;
            }
          }
        }
      }

      if (isValid) {
        this.error = false;
      } else if (this.error && this.$refs.input !== document.activeElement) {
        this.errorBounce = true;
      } else {
        this.error = true;
      }
    },

    mouseDownHandler: function (event) {
      if (!this.disabled) {
        this.clickPosition = Math.round(event.offsetX / this.$refs.input.clientWidth * 100) + '%';
      }
    },

    blurHandler: function () {
      if (!this.disabled) {
        this.active = false;
        this.clickPosition = '50%';
      }
    },

    focusHandler: function () {
      if (!this.disabled) {
        this.active = true;
        this.$emit('focus');
      }
    },

    errorClickHandler: function () {
      this.error = false;
      this.hasChanged = false;
    },

    errorAnimationEndHandler: function () {
      this.errorBounce = false;
    },

    dateClearClickHandler: function () {
      this.currentValue = '';
    },
  }
}
</script>

<style>
.text_input--container {
  --click-position: 50%;
  --error-color: 183, 28, 28;
  --disabled-color: rgba(0, 0, 0, 0.08);

  box-sizing: border-box;
  display: inline-block;
  min-width: 231px;
  position: relative;
  z-index: 1;
}

.text_input--container-active,
.text_input--container-deactivate {
  z-index: 2;
}

.text_input--container-wide {
  width: 100%;
}

.text_input--container input,
.text_input--container textarea {
  background: transparent;
  border: none;
  border-radius: 4px;
  box-sizing: border-box;
  caret-color: rgb(var(--primary-color, 0, 121, 102));
  color: rgba(0, 0, 0, 0.87);
  font-size: 1em;
  margin: 2px;
  padding: 1em 1em 0.857em 1em;
  width: 100%;

  -webkit-appearance: none;
}

.text_input--container textarea {
  width: calc(100% - 2px);
}

.text_input--container input:-webkit-autofill,
.text_input--container input:-webkit-autofill:hover,
.text_input--container input:-webkit-autofill:focus,
.text_input--container textarea:-webkit-autofill,
.text_input--container textarea:-webkit-autofill:hover,
.text_input--container textarea:-webkit-autofill:focus {
  box-shadow: 0 0 1em 0.625em rgb(var(--bg-color, 255, 255, 255)) inset;
  width: calc(100% - 4px);
}

.text_input--container:not(.text_input--container-filled) input::-webkit-datetime-edit-year-field,
.text_input--container:not(.text_input--container-filled) input::-webkit-datetime-edit-month-field,
.text_input--container:not(.text_input--container-filled) input::-webkit-datetime-edit-day-field,
.text_input--container:not(.text_input--container-filled) input::-webkit-datetime-edit {
  color: transparent;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.text_input--container:not(.text_input--container-disabled) input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  margin: 0;
}

.text_input--date_clear {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  Width: 32px;
  display: grid;
  align-content: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), visibility 1ms linear 150ms;
}

.text_input--container .help ~ input[type="date"] ~ .text_input--date_clear,
.text_input--container input[type="date"] ~ .help ~ .text_input--date_clear,
.text_input--container .help ~ input[type="datetime-local"] ~ .text_input--date_clear,
.text_input--container input[type="datetime-local"] ~ .help ~ .text_input--date_clear {
  right: 2.5rem;
}

.text_input--container-filled .text_input--date_clear {
  opacity: 1;
  visibility: visible;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), visibility 1ms linear;
}

.text_input--date_clear::before {
  content: 'clear';
}

.text_input--container-bg input {
  padding-bottom: 0.75em;
  padding-top: 1.5em;
}

.text_input--label {
  display: flex;
  align-items: center;
  background-color: rgb(var(--bg-color, 255, 255, 255));
  color: rgba(0, 0, 0, 0.54);
  left: 0.75em;
  max-width: calc(100% - 1.5em);
  overflow: hidden;
  padding: 0 0.25em;
  position: absolute;
  text-overflow: ellipsis;
  top: 50%;
  transform: translateY(-50%);
  transform-origin: 0 50%;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1), width 150ms cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  z-index: -1;
}

.text_input--container-bg .text_input--label {
  background-color: transparent;
}

.text_input--container-active .text_input--label {
  background: linear-gradient(to top, rgb(var(--bg-color, 255, 255, 255)) 0%, rgb(var(--bg-color, 255, 255, 255)) 60%, transparent 61%, transparent 100%);
  color: rgb(var(--primary-color, 0, 121, 102));
}

.text_input--container-active .text_input--label,
.text_input--container-filled .text_input--label {
  z-index: 1;
}

.text_input--container-filled:not(.text_input--container-active) .text_input--label {
  color: rgba(0, 0, 0, 0.54);
}

.text_input--container-error:not(.text_input--container-disabled) .text_input--label {
  background-color: transparent;
  color: rgb(var(--error-color, 183, 28, 28));
}

.text_input--container-disabled .text_input--label {
  background: transparent;
}

.text_input--container.text_input-container-disabled.text_input--container-filled .text_input--label {
  background: linear-gradient(to bottom, rgb(var(--bg-color, 255, 255, 255)) 0%, rgb(var(--bg-color, 255, 255, 255)) 30%, transparent 100%);
}

.text_input--container-error.text_input--container-active:not(.text_input--container-disabled) .text_input--label,
.text_input--container-error.text_input--container-filled:not(.text_input--container-disabled) .text_input--label {
  background: linear-gradient(to bottom, rgb(var(--bg-color, 255, 255, 255)) 0%, rgb(var(--bg-color, 255, 255, 255)) 30%, transparent 100%);
}

.text_input--container-active .text_input--label,
.text_input--container-filled:not(.text_input--container-active) .text_input--label {
  transform: translateY(-50%) translateY(-1.6em) scale(0.8);
}

.text_input--container-bg.text_input--container-active .text_input--label,
.text_input--container-bg:not(.text_input--container-active) .text_input--label {
  transform: translateY(-50%) translateY(-0.875em) scale(0.8);
}

/*SELECT SEARCH INPUT*/
.select--search_input {
  width: 100%;
  z-index: 1 !important;
}

.select--search_input .text_input--label {
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) !important;
  transform: translateY(-50%) !important;
}

.text_input--container.select--search_input.text_input--container-active .text_input--label,
.text_input--container.select--search_input.text_input--container-filled:not(.text_input--container-active) .text_input--label {
  opacity: 0;
}

.text_input--container::before {
  background-color: rgb(var(--bg-color, 255, 255, 255));
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  box-sizing: border-box;
  content: '';
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  transition: border-color 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  z-index: -2;
}


.text_input--container-disabled::before {
  background-color: var(--disabled-color);
  border-color: rgba(0, 0, 0, 0.54);
}

.text_input--container.select--search_input::before {
  border-left-width: 0;
  border-right-width: 0;
}

.text_input--container-bg::before {
  border-width: 0 0 2px 0;
  border-radius: 0;
}

.text_input--container-bg:not(.text_input--container-disabled):hover::before {
  background-color: rgba(0, 0, 0, 0.06);
}

.text_input--container-bg.text_input--container-active::before {
  background-color: rgba(0, 0, 0, 0.12);
}

.text_input--container:not(.text_input--container-disabled):hover::before {
  border-color: rgba(0, 0, 0, 0.34);
}

.text_input--container-error:not(.text_input--container-disabled):hover::before {
  border-color: rgba(var(--error-color, 183, 28, 28), 0.34);
}

.text_input--container-active:not(.text_input--container-disabled):not(.text_input--container-bg)::before {
  border-color: rgb(var(--primary-color, 0, 121, 102));
  border-width: 2px;
}

.text_input--container-error:not(.text_input--container-disabled)::before,
.text_input--container-active:not(.text_input--container-disabled):not(.text_input--container-bg).text_input--container-error::before {
  background-color: rgba(var(--error-color, 183, 28, 28), 0.06);
  border-color: rgba(var(--error-color, 183, 28, 28), 0.34);
}


.text_input--container-bg::after {
  background-color: rgb(var(--primary-color, 0, 121, 102));
  bottom: 0;
  content: '';
  height: 2px;
  left: 0;
  opacity: 0;
  position: absolute;
  transform: scaleX(0);
  transform-origin: var(--click-position, 50%) 50%;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
}

.text_input--container-bg.text_input--container-error::after {
  background-color: rgb(var(--error-color, 183, 28, 28));
}

.text_input--container-active:not(.text_input--container-disabled)::after {
  animation: text_input-ripple-scale-in 225ms forwards;
  transform: scaleX(1);
}

@keyframes text_input-ripple-scale-in {
  from {
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0;
    transform: scaleX(0);
  }
  35% {
    opacity: 1;
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}


/*START ERROR MESSAGE CODE*/
.text_input--error {
  position: absolute;
  left: 10%;
  color: rgba(var(--error-color, 0, 0, 0), 0.87);
  background: #FBFBFB;
  vertical-align: middle;
  max-width: 72%;
  padding: 6px 8px 4px 8px;
  top: 95%;
  border: 1px solid rgba(var(--error-color, 0, 0, 0), 1);
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, .12);
  cursor: default;
  z-index: 8;

  -webkit-border-radius: 4px;
  border-radius: 4px;

  -webkit-transform: scale(0);
  transform: scale(0);

  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1);

  opacity: 0;
}

.text_input--error:hover {
  background-color: #EEE;
}

.text_input--container-error .text_input--error {
  transform: scale(1);

  opacity: 1;
  cursor: pointer;
}

.text_input--container-error.text_input--container-error_bounce .text_input--error {
  -webkit-animation: text_input-error-bounce 250ms 1ms 2 ease-in-out;
  animation: text_input-error-bounce 250ms 1ms 2 ease-in-out;
}

.text_input--error_icon {
  vertical-align: middle;
  margin-right: 5px;
  margin-top: -3px;
  color: #FFA500;
}

.text_input--error::before,
.text_input--error::after {
  position: absolute;
  content: "";
  top: -5px;
  left: 10px;
  width: 0;
  height: 0;
  z-index: 4;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #FBFBFB;

  -webkit-transition: border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transition: border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.text_input--error::before {
  top: -6px;
  z-index: 2;
  border-bottom: 6px solid rgba(var(--error-color, 0, 0, 0), 1);
}

.text_input--error:hover::after {
  border-bottom-color: #EEE;
}

@keyframes text_input-error-bounce {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}
</style>